import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import api from "../Helper/api";
import { formatDate } from "../i18n";
import { downloadTravelAllowanceExcel, formatAmount } from "../Helper/travelAllowance";

const STATES = ["PENDING", "APPROVED", "REJECTED"];

const stateBadgeClass = {
  PENDING: "bg-yellow-50 text-yellow-600 border-yellow-200",
  APPROVED: "bg-green-50 text-green-600 border-green-200",
  REJECTED: "bg-red-50 text-red-600 border-red-200",
};

function TravelAllowanceListPage() {
  const { t } = useTranslation();
  const auth = useSelector((state) => state.auth.loginUser);
  const isAdmin = auth?.userType === "admin";
  const [allowances, setAllowances] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterProject, setFilterProject] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");

  const fetchAllowances = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/travel-allowances");
      setAllowances(res.data || []);
    } catch (error) {
      toast.error(t("travelAllowance.listLoadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAllowances();
  }, [fetchAllowances]);

  const filtered = allowances.filter((a) => {
    if (filterProject && !(a.project || "").toLowerCase().includes(filterProject.toLowerCase())) {
      return false;
    }

    if (filterStatus && a.state !== filterStatus) {
      return false;
    }

    if (filterEmployee && isAdmin) {
      const lower = filterEmployee.toLowerCase();
      const fullName = `${a.owner?.name} ${a.owner?.surname}`.toLowerCase();
      const tc = a.owner?.tc || "";
      return fullName.includes(lower) || tc.includes(lower);
    }

    return true;
  });

  const showError = (error) => toast.error(error.response?.data?.message || t("travelAllowance.genericError"));

  const handleApprove = async (id) => {
    if (!window.confirm(t("travelAllowance.confirmApprove"))) return;
    try {
      const res = await api.patch(`/travel-allowances/approve/${id}`);
      if (res.data.state) {
        toast.success(t("travelAllowance.approveSuccess"));
        fetchAllowances();
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt(t("list.rejectReasonPrompt"));
    if (reason === null) return; // cancelled
    if (!reason.trim()) {
      toast.error(t("travelAllowance.rejectReasonRequired"));
      return;
    }

    try {
      const res = await api.patch(`/travel-allowances/reject/${id}`, { rejectReason: reason });
      if (res.data.state) {
        toast.success(t("travelAllowance.rejectSuccess"));
        fetchAllowances();
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("travelAllowance.confirmDelete"))) return;
    try {
      const res = await api.delete(`/travel-allowances/${id}`);
      if (res.data.state) {
        toast.success(t("travelAllowance.deleteSuccess"));
        fetchAllowances();
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleDownloadExcel = async (allowance) => {
    toast.info(t("travelAllowance.excelPreparing"));
    try {
      await downloadTravelAllowanceExcel(allowance);
      toast.success(t("travelAllowance.excelSuccess"));
    } catch (error) {
      console.error("Excel download failed:", error);
      toast.error(t("travelAllowance.excelError"));
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">{t("travelAllowance.listTitle")}</h1>
          <p className="text-slate-500 mt-1">{t("travelAllowance.listDesc")}</p>
        </div>
        {isAdmin && (
          <Link
            to="/yeni-harcirah"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-2.5 rounded-lg shadow-lg shadow-blue-600/20 transition flex items-center gap-2"
          >
            <span>+</span> {t("travelAllowance.create")}
          </Link>
        )}
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-xl shadow-md border border-slate-100 mb-6 flex flex-wrap gap-4 items-center">
        {isAdmin && (
          <div className="flex flex-col gap-1 min-w-[200px] flex-1">
            <input
              type="text"
              id="travel-allowance-filter-employee"
              name="filterEmployee"
              aria-label={t("travelAllowance.filterEmployee")}
              placeholder={t("travelAllowance.filterEmployee")}
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        )}

        <div className="flex flex-col gap-1 min-w-[200px] flex-1">
          <input
            type="text"
            id="travel-allowance-filter-project"
            name="filterProject"
            aria-label={t("travelAllowance.filterProject")}
            placeholder={t("travelAllowance.filterProject")}
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="flex flex-col gap-1 w-44">
          <select
            id="travel-allowance-filter-status"
            name="filterStatus"
            aria-label={t("travelAllowance.status")}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
          >
            <option value="">{t("travelAllowance.statusAll")}</option>
            {STATES.map((state) => (
              <option key={state} value={state}>
                {t(`leaveState.${state}`)}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={() => {
            setFilterProject("");
            setFilterStatus("");
            setFilterEmployee("");
          }}
          className="border border-slate-300 text-slate-600 hover:bg-slate-50 px-4 py-2 rounded-lg text-sm transition"
        >
          {t("list.reset")}
        </button>
      </div>

      {/* List Container */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">{t("travelAllowance.loadingList")}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white p-12 text-center rounded-xl shadow-md border border-slate-100 text-slate-500">
          {t("travelAllowance.empty")}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 text-slate-600 text-xs font-semibold uppercase border-b border-slate-100">
                  {isAdmin && <th className="px-6 py-4">{t("travelAllowance.colEmployee")}</th>}
                  <th className="px-6 py-4">{t("travelAllowance.project")}</th>
                  <th className="px-6 py-4">{t("travelAllowance.travelDates")}</th>
                  <th className="px-6 py-4 w-24 text-center">{t("travelAllowance.days")}</th>
                  <th className="px-6 py-4 text-right">{t("travelAllowance.totalAmountShort")}</th>
                  <th className="px-6 py-4 w-32 text-center">{t("travelAllowance.status")}</th>
                  <th className="px-6 py-4 text-right min-w-[280px]">{t("travelAllowance.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filtered.map((allowance) => {
                  const isPending = allowance.state === "PENDING";
                  const isApproved = allowance.state === "APPROVED";

                  return (
                    <tr key={allowance._id} className="hover:bg-slate-50/50 transition">
                      {isAdmin && (
                        <td className="px-6 py-4">
                          <div className="font-semibold text-slate-800">
                            {allowance.owner?.name} {allowance.owner?.surname}
                          </div>
                          <div className="text-xs text-slate-400">
                            {t("dashboard.tckn")}: {allowance.owner?.tc}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-700">{allowance.project}</div>
                        {allowance.note && (
                          <div className="text-xs text-slate-400 line-clamp-1 max-w-xs">{allowance.note}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {formatDate(allowance.startDate) || "-"} - {formatDate(allowance.endDate) || "-"}
                      </td>
                      <td className="px-6 py-4 text-center text-slate-600 font-medium">
                        {allowance.totalDays}
                      </td>
                      <td className="px-6 py-4 text-right font-semibold text-slate-800">
                        {formatAmount(allowance.totalAmount)} ₺
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${stateBadgeClass[allowance.state] || ""}`}
                        >
                          {t(`leaveState.${allowance.state}`, { defaultValue: allowance.state || "-" })}
                        </span>
                      </td>
                      <td className="px-6 py-4 align-middle">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link
                            to={`/harcirah-detay/${allowance._id}`}
                            className="inline-flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-3 py-1.5 text-xs rounded-lg transition duration-200"
                          >
                            {t("travelAllowance.detail")}
                          </Link>

                          {isApproved && (
                            <button
                              onClick={() => handleDownloadExcel(allowance)}
                              className="inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1.5 text-xs rounded-lg shadow-sm hover:shadow transition duration-200"
                            >
                              {t("list.buttonExcel")} 📥
                            </button>
                          )}

                          {isAdmin && isPending && (
                            <>
                              <button
                                onClick={() => handleApprove(allowance._id)}
                                className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold px-3 py-1.5 text-xs rounded-lg shadow-sm hover:shadow transition duration-200"
                              >
                                {t("travelAllowance.approve")}
                              </button>
                              <button
                                onClick={() => handleReject(allowance._id)}
                                className="inline-flex items-center justify-center bg-amber-500 hover:bg-amber-600 text-white font-semibold px-3 py-1.5 text-xs rounded-lg shadow-sm hover:shadow transition duration-200"
                              >
                                {t("travelAllowance.reject")}
                              </button>
                            </>
                          )}

                          {(isAdmin || (isPending && allowance.owner?.tc === auth?.tc)) && (
                            <button
                              onClick={() => handleDelete(allowance._id)}
                              className="inline-flex items-center justify-center bg-rose-600 hover:bg-rose-700 text-white font-semibold px-3 py-1.5 text-xs rounded-lg shadow-sm hover:shadow transition duration-200"
                            >
                              {t("travelAllowance.delete")}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default TravelAllowanceListPage;
